import type { GameState, GhostState, PacState, Dir } from '@pacmates/engine';
import { COLORS, FRUIT_TIER_COLOR, ghostColor, ghostMarkerOp, pacColor, POWERUP_COLOR } from './colors';
import { lerpWrapAware } from './interpolate';

const FRIGHT_FLASH_WINDOW = 2.0;

const GHOST_SKIRT_LEGS = 4;
const GHOST_SKIRT_HEM_AMPLITUDE = 0.28;
/** ms per full hem-undulation cycle. */
const GHOST_SKIRT_HEM_PERIOD_MS = 220;

/**
 * The ghost body "skirt" polygon vertices, in path order, for the segment
 * that runs immediately after the dome arc. The caller draws
 * `ctx.arc(cx, cy - r*0.1, r, Math.PI, 0, false)` (which ends at
 * `(cx + r, cy - 0.1r)`), then `ctx.lineTo(...)` each of these points in
 * order (the first vertex is the bottom-right corner, at `x === cx + r`),
 * then `ctx.closePath()`.
 *
 * Walks strictly RIGHT-TO-LEFT (x is monotonically non-increasing, and the
 * last vertex lands exactly at `x === cx - r`) so `closePath()` runs
 * straight back up to the arc's start point `(cx - r, cy - 0.1r)` instead of
 * cutting a diagonal across a zigzag that ended on the right (the
 * self-intersecting "bitten wedge" defect: the old loop walked left-to-right
 * after an initial jump straight across the bottom, so `closePath()` had to
 * cut back across the whole body to reach the arc start).
 *
 * When `animated` is true the hem undulates via a sine wave keyed off
 * `nowMs` (period `GHOST_SKIRT_HEM_PERIOD_MS`); when false the hem is frozen
 * to a fixed phase regardless of `nowMs` (used by the reduced-motion unit
 * test -- in production `nowMs` itself is already the frozen decorative
 * clock from `motion.ts`, so `drawGhost` always passes `animated: true` and
 * relies on that upstream freeze rather than a second one here).
 */
export function ghostBodyPoints(
  cx: number,
  cy: number,
  r: number,
  nowMs: number,
  animated: boolean,
): { x: number; y: number }[] {
  const baseY = cy + r * 0.9;
  const legs = GHOST_SKIRT_LEGS;
  const phase = animated ? nowMs / GHOST_SKIRT_HEM_PERIOD_MS : 0;
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i <= legs; i++) {
    const lx = cx + r - (2 * r * i) / legs;
    // sin(...) in [-1, 1] -> wave in [-1, 0], so the hem only ever pulls
    // UP from baseY (never overshoots below it, matching the original
    // "teeth" silhouette) while still undulating continuously over time.
    const wave = (Math.sin(phase + i * (Math.PI / 2)) - 1) / 2;
    const ly = baseY + wave * r * GHOST_SKIRT_HEM_AMPLITUDE;
    points.push({ x: lx, y: ly });
  }
  return points;
}

function dirAngle(dir: Dir): number {
  switch (dir) {
    case 'up':
      return -Math.PI / 2;
    case 'down':
      return Math.PI / 2;
    case 'left':
      return Math.PI;
    default:
      return 0;
  }
}

function blend(prev: GameState | null, cur: GameState, frac: number, get: (s: GameState) => { x: number; y: number }[]): { x: number; y: number }[] {
  const curList = get(cur);
  if (!prev) return curList;
  const prevList = get(prev);
  if (prevList.length !== curList.length) return curList;
  return curList.map((c, i) => {
    const p = prevList[i]!;
    return {
      x: lerpWrapAware(p.x, c.x, frac, cur.cols),
      y: lerpWrapAware(p.y, c.y, frac, cur.rows),
    };
  });
}

export function renderEntities(
  ctx: CanvasRenderingContext2D,
  cur: GameState,
  prev: GameState | null,
  tile: number,
  frac: number,
  /** Decorative-animation clock (fright-flash blink phase). Gameplay
   * interpolation above always uses the real `frac`; only this clock is
   * frozen under prefers-reduced-motion -- see game/motion.ts. */
  nowMs: number = performance.now(),
  /** Solo-round display: `GameState.players` is always a fixed 2-tuple (the
   * engine still simulates a P2 pac parked at spawn even in a solo round),
   * so this is COSMETIC ONLY -- we simply choose not to paint player index
   * 1. Ghosts/pellets/P1 are unaffected. Defaults to true so any other
   * caller keeps drawing both pacs unless it opts out explicitly. */
  drawP2: boolean = true,
  /** PART 1: seat display names (lobby.seats[i].name), used ONLY for the
   * partner's name label -- the local player's own label is always "You"
   * (see pacLabelOps). Defaults keep every other/older caller unaffected. */
  p1Name: string = 'Player 1',
  p2Name: string = 'Player 2',
  /** Which seat THIS client is -- determines which pac gets "You". */
  mySeat: 1 | 2 = 1,
  /** PART 3: colorblind-safe palette + non-hue P2 marker -- see
   * game/colors.ts's `pacColor`/`COLORBLIND_COLORS`. Defaults to false so
   * any other/older caller keeps drawing the default palette. */
  colorblind: boolean = false,
): void {
  const pacPositions = blend(prev, cur, frac, (s) => s.players.map((p) => ({ x: p.x, y: p.y })));
  const ghostPositions = blend(prev, cur, frac, (s) => s.ghosts.map((g) => ({ x: g.x, y: g.y })));

  if (cur.fruit.active) drawFruit(ctx, cur, tile);
  if (cur.powerupOnMaze) drawPowerup(ctx, cur, tile);

  cur.ghosts.forEach((g, i) => drawGhost(ctx, g, ghostPositions[i]!, cur, tile, nowMs, colorblind));
  cur.players.forEach((p, i) => {
    if (i === 1 && !drawP2) return;
    drawPac(ctx, p, pacPositions[i]!, tile, colorblind);
  });

  // Labels drawn LAST (topmost layer) so they always read over walls/dots/
  // ghosts -- see pacLabelOps for the pure, testable position/clamp math.
  const canvasW = tile * cur.cols;
  const canvasH = tile * cur.rows;
  const labels = pacLabelOps(pacPositions, tile, canvasW, canvasH, [p1Name, p2Name], mySeat, drawP2, colorblind);
  for (const label of labels) drawPacLabel(ctx, label);
}

export interface PacLabelOp {
  text: string;
  /** Canvas-px CENTER x, clamped so the label's approximate rendered box
   * stays fully inside the canvas (a pac near the left/right edge doesn't
   * push its own label off-screen). */
  x: number;
  /** Canvas-px CENTER y, clamped the same way vertically (a pac near the
   * top edge doesn't push its label above the canvas). */
  y: number;
  /** PER-SCREEN 05 ROUND 3 fix: this seat's own pac color (P1 yellow / P2
   * green, respecting `colorblind`) -- drawPacLabel paints a small pill
   * behind the text in this color so "You"/the partner's name attributes to
   * the right pac at a glance, not just via reading the text itself. */
  color: string;
  /** Pill box size (canvas px, FULL width/height, not half) -- reuses the
   * same clamp-math box pacLabelOps already computes (LABEL_CHAR_WIDTH_PX /
   * LABEL_HEIGHT_PX) so drawPacLabel never needs `ctx.measureText` (keeps it
   * exercisable against a duck-typed mock ctx, same as the rest of this
   * file's draw functions -- see renderEntities.test.ts's makeMockCtx). */
  width: number;
  height: number;
}

// Rough per-character width for the small bold label font, used only for
// the CLAMP math below (keeps this pure/testable without a canvas 2D
// context's real measureText) -- the actual glyphs are narrower than this
// on average, so the clamp errs slightly conservative (label kept a hair
// further from the edge than strictly necessary) rather than ever letting
// a wide name clip off-canvas.
// PER-SCREEN 05 ROUND 3 fix: the label text itself was 10px -- legible up
// close but easy to miss at a glance next to the pac it names. Bumped the
// draw font (drawPacLabel below) 10px -> 13px and widened these CLAMP
// constants to match (6->7.5px/char, 12->17px tall) so the collision/edge
// math stays consistent with the actual larger rendered box instead of
// under-reserving space for it.
export const LABEL_CHAR_WIDTH_PX = 7.5;
export const LABEL_MIN_WIDTH_PX = 26; // floor so even "You" gets a sane clamp box
export const LABEL_HEIGHT_PX = 17;
// Gap between the pac's own top edge (cy - r) and the label's anchor,
// scaled by tile so it stays proportionate at every maze zoom level.
const LABEL_GAP_ABOVE_PAC = 0.55;
const LABEL_CANVAS_MARGIN = 2; // px, minimum clearance from any canvas edge
// Extra clearance enforced between two labels' boxes when they'd otherwise
// touch/overlap (DEFECT fix below) -- purely cosmetic breathing room, not a
// hard requirement like the canvas-edge margin above.
const LABEL_COLLISION_GAP_PX = 2;

function clamp(v: number, lo: number, hi: number): number {
  if (lo > hi) return (lo + hi) / 2; // degenerate (canvas narrower than the label) -- center it
  return Math.min(Math.max(v, lo), hi);
}

interface LabelCandidate {
  text: string;
  cx: number; // pac center x (canvas px), used to re-derive an ABOVE/BELOW placement
  cy: number; // pac center y (canvas px)
  r: number; // pac radius (canvas px)
  halfW: number;
  halfH: number;
  x: number;
  y: number;
  color: string;
}

function rectsOverlap(a: LabelCandidate, b: LabelCandidate): boolean {
  return (
    Math.abs(a.x - b.x) < a.halfW + b.halfW + LABEL_COLLISION_GAP_PX &&
    Math.abs(a.y - b.y) < a.halfH + b.halfH + LABEL_COLLISION_GAP_PX
  );
}

function clampCandidate(c: LabelCandidate, canvasW: number, canvasH: number): void {
  c.x = clamp(c.x, LABEL_CANVAS_MARGIN + c.halfW, canvasW - LABEL_CANVAS_MARGIN - c.halfW);
  c.y = clamp(c.y, LABEL_CANVAS_MARGIN + c.halfH, canvasH - LABEL_CANVAS_MARGIN - c.halfH);
}

/**
 * Pure derivation of the name-label draw ops for the shared maze (PART 1:
 * co-op legibility -- two near-identical pacs distinguished only by color
 * otherwise). Takes each pac's ALREADY-INTERPOLATED grid position (the same
 * values `renderEntities` computes via `blend()` above) plus the canvas
 * pixel size, so it is testable with plain numbers -- no canvas context
 * needed. Anchored above each pac, clamped to stay fully inside the canvas,
 * and the P2 label is omitted entirely when `drawP2` is false (solo round).
 *
 * DEFECT fix: both pacs spawn adjacent and, with no input drift, can sit on
 * the SAME tile (server-verified `[{id:1,x:6,y:23},{id:2,x:6,y:23}]`) --
 * both labels then anchor to the identical point and fully stack, hiding
 * whichever one draws first (in practice always the local player's own
 * "You", the one label they most need to see). When the two labels'
 * (already clamped) boxes would overlap, P2's label is deterministically
 * re-anchored BELOW its own pac instead of above; if that still overlaps
 * (only possible in a degenerate near-zero-height canvas) it is nudged
 * horizontally by their combined half-widths instead, toward whichever side
 * of the canvas has more room. Both labels stay independently clamped
 * inside the canvas at every step, same as before this fix.
 */
export function pacLabelOps(
  pacPositions: readonly { x: number; y: number }[],
  tile: number,
  canvasW: number,
  canvasH: number,
  names: readonly [string, string],
  mySeat: 1 | 2,
  drawP2: boolean,
  /** PER-SCREEN 05 ROUND 3: colorblind-safe palette toggle, same flag the
   * canvas pac/HUD already respect (`game/colors.ts`'s `pacColor`) -- only
   * affects each label's own pill `color`, never position/collision math.
   * Defaults to false so every existing caller/test (none of which pass
   * this arg) is unaffected. */
  colorblind: boolean = false,
): PacLabelOp[] {
  const r = tile * 0.42;
  const candidates: LabelCandidate[] = [];
  for (let i = 0; i < pacPositions.length; i++) {
    if (i === 1 && !drawP2) continue;
    const pos = pacPositions[i]!;
    const seat = (i + 1) as 1 | 2;
    const text = seat === mySeat ? 'You' : names[i] || `P${seat}`;
    const cx = pos.x * tile + tile / 2;
    const cy = pos.y * tile + tile / 2;
    const halfW = Math.max(LABEL_MIN_WIDTH_PX, text.length * LABEL_CHAR_WIDTH_PX) / 2;
    const halfH = LABEL_HEIGHT_PX / 2;
    const c: LabelCandidate = {
      text,
      cx,
      cy,
      r,
      halfW,
      halfH,
      x: cx,
      y: cy - r - tile * LABEL_GAP_ABOVE_PAC,
      color: pacColor(seat, colorblind),
    };
    clampCandidate(c, canvasW, canvasH);
    candidates.push(c);
  }

  if (candidates.length === 2 && rectsOverlap(candidates[0]!, candidates[1]!)) {
    const p1 = candidates[0]!;
    const p2 = candidates[1]!;

    // Attempt 1: flip P2 to sit BELOW its own pac instead of above.
    p2.y = p2.cy + p2.r + tile * LABEL_GAP_ABOVE_PAC;
    clampCandidate(p2, canvasW, canvasH);

    if (rectsOverlap(p1, p2)) {
      // Attempt 2 (degenerate canvas -- above/below both clamp to the same
      // band): nudge P2 horizontally by the combined half-widths, toward
      // whichever side of the canvas has more room, so the two boxes are
      // guaranteed disjoint on the x-axis regardless of y.
      const roomRight = canvasW - (p2.cx + p2.halfW);
      const roomLeft = p2.cx - p2.halfW;
      const nudge = p1.halfW + p2.halfW + LABEL_COLLISION_GAP_PX;
      p2.x = roomRight >= roomLeft ? p2.cx + nudge : p2.cx - nudge;
      clampCandidate(p2, canvasW, canvasH);
    }
  }

  return candidates.map((c) => ({ text: c.text, x: c.x, y: c.y, color: c.color, width: c.halfW * 2, height: c.halfH * 2 }));
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Small, high-contrast label: a dark stroked halo behind light fill text so
 * it reads over walls/dots/ghosts regardless of what's underneath -- same
 * "additive glow around a preserved solid core" idiom the rest of this file
 * uses (see drawPac/drawGhost), just via strokeText's halo instead of
 * shadowBlur (canvas shadowBlur doesn't apply to text stroke width the way
 * it does to fills, so a real outline reads more crisply at this size).
 * PER-SCREEN 05 ROUND 3 fix: bumped 10px -> 13px (see LABEL_* constants'
 * own comment) and added a translucent pill in the seat's own pac color
 * behind the text -- "small pill bg in each player's color" was the
 * critique's explicit suggested fix for at-a-glance attribution, on top of
 * (not instead of) the existing dark-halo text, which stays untouched so
 * legibility over any maze content underneath is unaffected. */
function drawPacLabel(ctx: CanvasRenderingContext2D, op: PacLabelOp): void {
  ctx.save();
  const left = op.x - op.width / 2;
  const top = op.y - op.height / 2;
  const radius = op.height / 2;
  ctx.beginPath();
  ctx.moveTo(left + radius, top);
  ctx.arcTo(left + op.width, top, left + op.width, top + op.height, radius);
  ctx.arcTo(left + op.width, top + op.height, left, top + op.height, radius);
  ctx.arcTo(left, top + op.height, left, top, radius);
  ctx.arcTo(left, top, left + op.width, top, radius);
  ctx.closePath();
  // Fill only (no `ctx.stroke()` outline) -- a soft translucent pill is
  // enough signal on its own, and keeps this draw op entirely out of the
  // ghost-marker `stroke`-call counting in renderEntities.test.ts (labels
  // draw AFTER all 4 ghosts, so an extra stroke() here would land inside
  // the last ghost's counted window and inflate its ring count).
  ctx.fillStyle = hexToRgba(op.color, 0.34);
  ctx.fill();

  ctx.font = '800 13px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(4, 6, 20, 0.9)';
  ctx.lineWidth = 3;
  ctx.strokeText(op.text, op.x, op.y);
  ctx.fillStyle = COLORS.text;
  ctx.fillText(op.text, op.x, op.y);
  ctx.restore();
}

function drawFruit(ctx: CanvasRenderingContext2D, s: GameState, tile: number): void {
  const { x, y, tierIndex } = s.fruit;
  const cx = x * tile + tile / 2;
  const cy = y * tile + tile / 2;
  ctx.fillStyle = FRUIT_TIER_COLOR[tierIndex] ?? '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, cy, tile * 0.34, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = COLORS.text;
  ctx.lineWidth = Math.max(1, tile * 0.03);
  ctx.stroke();
}

function drawPowerup(ctx: CanvasRenderingContext2D, s: GameState, tile: number): void {
  const pk = s.powerupOnMaze!;
  const cx = pk.x * tile + tile / 2;
  const cy = pk.y * tile + tile / 2;
  ctx.fillStyle = POWERUP_COLOR[pk.kind] ?? '#ffffff';
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI / 4);
  const r = tile * 0.28;
  ctx.fillRect(-r, -r, r * 2, r * 2);
  ctx.restore();
}

function drawPac(
  ctx: CanvasRenderingContext2D,
  p: PacState,
  pos: { x: number; y: number },
  tile: number,
  colorblind: boolean = false,
): void {
  const cx = pos.x * tile + tile / 2;
  const cy = pos.y * tile + tile / 2;
  const r = tile * 0.42;
  const color = pacColor(p.id, colorblind);

  if (!p.alive) {
    ctx.globalAlpha = 0.35;
  }

  const mouth = p.dir === 'none' ? 0.15 : 0.15 + 0.2 * Math.abs(Math.sin(p.mouthPhase));
  const angle = dirAngle(p.dir === 'none' ? p.desired : p.dir);

  // Soft colored bloom BEHIND the body; exact identity hex painted last on
  // top at shadowBlur=0 (plan-gen1.md Experiment 2 "additive around a
  // preserved solid core" -- same idiom as the energizer/wall rim).
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = tile * 0.22;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.arc(cx, cy, r, angle + mouth * Math.PI, angle - mouth * Math.PI + Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.arc(cx, cy, r, angle + mouth * Math.PI, angle - mouth * Math.PI + Math.PI * 2);
  ctx.closePath();
  ctx.fill();

  if ((p.powerups.phase ?? 0) > 0) {
    ctx.strokeStyle = color;
    ctx.globalAlpha = (p.alive ? 1 : 0.35) * 0.6;
    ctx.lineWidth = Math.max(1, tile * 0.05);
    ctx.beginPath();
    ctx.arc(cx, cy, r + tile * 0.12, 0, Math.PI * 2);
    ctx.stroke();
  }
  if ((p.powerups.speed ?? 0) > 0) {
    ctx.fillStyle = '#fff064';
    ctx.beginPath();
    ctx.arc(cx - r * 0.9, cy, tile * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }

  // PART 3: colorblind mode's non-hue P2 marker -- a dashed white ring
  // outside the body (a DIFFERENT radius/style than the phase-powerup ring
  // above, which is drawn in the player's OWN color and only while phase is
  // active, so the two never read as the same feature). This one is always
  // on for P2 whenever colorblind mode is on, regardless of powerups, so
  // "which pac is mine" never depends on hue perception alone -- shape/
  // pattern alone is enough even with color entirely removed.
  if (colorblind && p.id === 2) {
    ctx.save();
    ctx.globalAlpha = p.alive ? 1 : 0.35;
    ctx.strokeStyle = COLORS.text;
    ctx.lineWidth = Math.max(1, tile * 0.045);
    ctx.setLineDash([tile * 0.06, tile * 0.06]);
    ctx.beginPath();
    ctx.arc(cx, cy, r + tile * 0.2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  ctx.globalAlpha = 1;
}

function drawGhost(
  ctx: CanvasRenderingContext2D,
  g: GhostState,
  pos: { x: number; y: number },
  s: GameState,
  tile: number,
  nowMs: number,
  /** PART 3 (ghosts): colorblind-safe identity palette + non-hue outline
   * marker -- see game/colors.ts's `ghostColor`/`COLORBLIND_GHOST_COLORS`/
   * `ghostMarkerOp`. Defaults to false so any other/older caller keeps
   * drawing the default palette with no marker. */
  colorblind: boolean = false,
): void {
  const cx = pos.x * tile + tile / 2;
  const cy = pos.y * tile + tile / 2;
  const r = tile * 0.42;

  if (g.mode === 'eaten') {
    drawEyes(ctx, cx, cy, r, g.dir);
    return;
  }

  let bodyColor: string;
  const softGlow = ghostColor(g.name, colorblind);
  if (g.mode === 'frightened') {
    const flashing = s.frightLeft > 0 && s.frightLeft <= FRIGHT_FLASH_WINDOW;
    const flashOn = flashing ? Math.floor(nowMs / 150) % 2 === 0 : false;
    bodyColor = flashing && flashOn ? COLORS.frightFlash : COLORS.fright;
  } else {
    bodyColor = softGlow;
  }

  // Single source of truth for the body outline (glow pass and solid-core
  // pass MUST stay geometrically identical or the bloom won't register with
  // the body -- plan-gen1.md Experiment 2, "additive around a preserved
  // solid core"). `animated: true` is safe here because `nowMs` is already
  // the frozen decorative clock from `motion.ts` under reduced-motion.
  const skirt = ghostBodyPoints(cx, cy, r, nowMs, true);

  // Soft colored bloom BEHIND the body; the exact identity/fright hex is
  // painted last on top at shadowBlur=0 so tolerance-6 pixel asserts still
  // land on the true core color.
  ctx.save();
  ctx.shadowColor = bodyColor;
  ctx.shadowBlur = tile * 0.22;
  ctx.fillStyle = bodyColor;
  ctx.beginPath();
  ctx.arc(cx, cy - r * 0.1, r, Math.PI, 0, false);
  for (const pt of skirt) ctx.lineTo(pt.x, pt.y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = bodyColor;
  ctx.beginPath();
  ctx.arc(cx, cy - r * 0.1, r, Math.PI, 0, false);
  for (const pt of skirt) ctx.lineTo(pt.x, pt.y);
  ctx.closePath();
  ctx.fill();

  // PART 3 (ghosts) task requirement #3: hue alone is what's broken (that's
  // the whole defect), so a non-hue cue is MANDATORY, not optional. Every
  // ghost in colorblind mode also gets a deterministic outline pattern
  // (solid/dashed/dotted/double, per game/colors.ts's `ghostMarkerOp`) drawn
  // in a neutral (non-identity) color so it reads in pure greyscale --
  // identity never depends on hue perception alone. Skipped while
  // frightened: classic arcade rules make every frightened ghost equally
  // edible regardless of identity, so there is nothing to disambiguate, and
  // adding a marker there would risk muddying the "frightened = blue" read
  // task requirement #4 explicitly protects.
  if (colorblind && g.mode !== 'frightened') {
    const marker = ghostMarkerOp(g.name);
    ctx.save();
    ctx.strokeStyle = COLORS.text;
    ctx.globalAlpha = 0.65;
    ctx.lineWidth = Math.max(1, tile * 0.035);
    ctx.lineCap = marker.style === 'dotted' ? 'round' : 'butt';
    ctx.setLineDash(marker.dash.map((d) => d * r));
    for (let ring = 0; ring < marker.rings; ring++) {
      const ringR = marker.rings === 2 ? r * (ring === 0 ? 1.14 : 0.96) : r * 1.14;
      ctx.beginPath();
      ctx.arc(cx, cy - r * 0.1, ringR, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  if (g.mode !== 'frightened') {
    drawEyes(ctx, cx, cy - r * 0.1, r, g.dir);
  } else {
    // DEFECT 12 fix: classic-arcade "frightened" also puts plain round
    // white eyes on the body (no directional pupil -- a frightened ghost
    // isn't "looking" anywhere) IN ADDITION TO the pale scared-mouth line
    // below. Two independent white facial features read as "edible" at a
    // glance regardless of exactly which blue the body ends up being, and
    // can never be confused with a normal ghost's colored-pupil eyes.
    const ex = r * 0.32;
    const ey = -r * 0.1 - r * 0.15;
    for (const side of [-1, 1]) {
      ctx.fillStyle = COLORS.frightFlash;
      ctx.beginPath();
      ctx.arc(cx + side * ex, cy + ey, r * 0.16, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = COLORS.frightFlash;
    ctx.lineWidth = Math.max(1, tile * 0.04);
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.5, cy + r * 0.25);
    ctx.lineTo(cx - r * 0.25, cy + r * 0.05);
    ctx.lineTo(cx, cy + r * 0.25);
    ctx.lineTo(cx + r * 0.25, cy + r * 0.05);
    ctx.lineTo(cx + r * 0.5, cy + r * 0.25);
    ctx.stroke();
  }
}

function drawEyes(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, dir: Dir): void {
  const dx = dir === 'left' ? -1 : dir === 'right' ? 1 : 0;
  const dy = dir === 'up' ? -1 : dir === 'down' ? 1 : 0;
  const ex = r * 0.32;
  const ey = -r * 0.15;
  for (const side of [-1, 1]) {
    ctx.fillStyle = COLORS.eyes;
    ctx.beginPath();
    ctx.arc(cx + side * ex, cy + ey, r * 0.22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = COLORS.pupil;
    ctx.beginPath();
    ctx.arc(cx + side * ex + dx * r * 0.09, cy + ey + dy * r * 0.09, r * 0.1, 0, Math.PI * 2);
    ctx.fill();
  }
}
